import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import React from 'react';
import Modal from '@/components/modal/Modal';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import { colors } from '@/lib/colors';
import { createColumnHelper } from '@tanstack/react-table';
import { GetCoachPermissionResponse } from '@/api/center';
import Table from '@/components/Table/Table';
import useSingleQuery from '@/hook/useSingleQuery';
import { fetchCoachPermission } from '@/services/useCenterQueryService';
import { Col, getItemWithExpiration, Row } from '@/lib/utils';
import { Toggle } from '@/components/Toggle';
import SvgIcon from '@/components/SvgIcon';
import { TooltipWrapper } from '@/components/TooltipWrapper';
import { cloneDeep, set } from 'lodash';
import useCenterMutationService from '@/services/useCenterMutationService';
import { useQueryClient } from '@tanstack/react-query';
import ModalPermissionAlert from '@/components/modal/ModalPermissionAlert';
import { log } from 'console';
import ModalPermissionSuccessAlert from '@/components/modal/ModalPermissionSuccessAlert';

type Props = {
    setIsVisible: Dispatch<SetStateAction<boolean>>;
    onSucessPermissionChange?: () => void;
};

const ModalCoachPermission = ({ setIsVisible, onSucessPermissionChange }: Props) => {
    const [authorization, setAuthorization] = useState(getItemWithExpiration('authorization'));
    const [authObject, setAuthObject] = useState(getItemWithExpiration('authObject'));
    const queryClient = useQueryClient();

    // 변경 경고 모달 여부
    const [isChangeAlert, setIsChangeAlert] = useState<boolean>(false);
    // 변경 성공 모달 여부
    const [isSuccessAlert, setIsSuccessAlert] = useState<boolean>(false);

    const { refinedResult: result } = useSingleQuery({
        fetchFn: fetchCoachPermission,
        params: { centerId: authObject?.centerId ?? '' },
        enabled: !!authorization && !!authObject?.centerId,
        suspense: false,
    });

    const { patchCoachPermissionMutation } = useCenterMutationService({});

    //     isMembershipPermissionAll : 수강권 발급 권한
    // isMembershipPermissionUpdate : 수강권 수정 권한
    // isPastSchedulePermissionUpdate : 지난 스케줄 수정 권한
    const coachPermissionList = (result?.data as any)?.coachPermissions ?? [];

    // clone
    const [coachPermissionListClone, setCoachPermissionListClone] = useState<any[]>([]);

    // coachPermissionList가 변경될 때 clone 업데이트
    React.useEffect(() => {
        setCoachPermissionListClone(cloneDeep(coachPermissionList));
    }, [coachPermissionList]);

    const handleToggleChange = (rowIndex: number, field: string, value: boolean) => {
        const updatedList = cloneDeep(coachPermissionListClone);
        updatedList[rowIndex] = {
            ...updatedList[rowIndex],
            [field]: value,
        };
        setCoachPermissionListClone(updatedList);
    };

    const handleApplyChanges = async () => {
        const updatedPermissions = coachPermissionListClone.map((item) => ({
            coachId: item.coachId,
            isMembershipPermissionAll: item.isMembershipPermissionAll,
            isMembershipPermissionUpdate: item.isMembershipPermissionUpdate,
            isPastSchedulePermissionUpdate: item.isPastSchedulePermissionUpdate,
        }));

        await patchCoachPermissionMutation.mutateAsync({
            centerId: authObject?.centerId ?? '',
            permissions: updatedPermissions,
        });

        queryClient.invalidateQueries({
            queryKey: ['getCoachPermissionQuery', authObject?.centerId],
        });
        onSucessPermissionChange && onSucessPermissionChange();
        setIsChangeAlert(false);
        setIsSuccessAlert(true);
    };

    const columnHelper = createColumnHelper<GetCoachPermissionResponse>();

    const columns = useMemo(
        () => [
            // 강사이름
            columnHelper.accessor('coachName', {
                id: 'coachName',
                header: '강사',
                cell: (info) => info.getValue(),
                size: 200,
                enableSorting: false,
            }),
            // 강사 이메일
            columnHelper.accessor('isMembershipPermissionAll', {
                id: 'isMembershipPermissionAll',
                header: () => (
                    <Row className="flex items-center">
                        <RegularText fontSize={16} lineHeight={20} color={colors.Text_1} className="mr-[10px]">
                            수강권 발급 권한
                        </RegularText>
                        <TooltipWrapper
                            tooltip={{
                                opacity: 1,
                                customContent: (
                                    <Col>
                                        <DemiLightText fontSize={13} lineHeight={18} color={colors.white}>
                                            {`* 해당 권한이 켜진 경우, `}
                                            <MediumText fontSize={13} lineHeight={18} color={colors.white}>
                                                강사가 수강권을
                                            </MediumText>
                                        </DemiLightText>
                                        <MediumText fontSize={13} lineHeight={18} color={colors.white}>
                                            발급할 수 있습니다.
                                        </MediumText>
                                    </Col>
                                ),
                            }}
                        >
                            <SvgIcon name={'SvgInfoInCircle'} size={16} fill={'#B4BCC4'} />
                        </TooltipWrapper>
                    </Row>
                ),
                cell: (info) => {
                    const rowIndex = info.row.index;
                    const currentValue =
                        coachPermissionListClone[rowIndex]?.isMembershipPermissionAll ?? (info.getValue() as boolean);
                    return (
                        <Toggle
                            value={currentValue}
                            onToggle={(value) => {
                                handleToggleChange(rowIndex, 'isMembershipPermissionAll', value);
                            }}
                        />
                    );
                },
                size: 200,
                enableSorting: false,
            }),
            columnHelper.accessor('isMembershipPermissionUpdate', {
                id: 'isMembershipPermissionUpdate',
                header: () => (
                    <Row className="flex items-center">
                        <RegularText fontSize={16} lineHeight={20} color={colors.Text_1} className="mr-[10px]">
                            수강권 수정 권한
                        </RegularText>
                        <TooltipWrapper
                            tooltip={{
                                opacity: 1,
                                customContent: (
                                    <Col>
                                        <DemiLightText fontSize={13} lineHeight={18} color={colors.white}>
                                            {`* 해당 권한이 켜진 경우, `}
                                            <MediumText fontSize={13} lineHeight={18} color={colors.white}>
                                                강사가 수강권을
                                            </MediumText>
                                        </DemiLightText>
                                        <MediumText fontSize={13} lineHeight={18} color={colors.white}>
                                            수정(수정/만료/삭제)할 수 있습니다.
                                        </MediumText>
                                    </Col>
                                ),
                            }}
                        >
                            <SvgIcon name={'SvgInfoInCircle'} size={16} fill={'#B4BCC4'} />
                        </TooltipWrapper>
                    </Row>
                ),
                cell: (info) => {
                    const rowIndex = info.row.index;
                    const currentValue =
                        coachPermissionListClone[rowIndex]?.isMembershipPermissionUpdate ??
                        (info.getValue() as boolean);
                    return (
                        <Toggle
                            value={currentValue}
                            onToggle={(value) => {
                                handleToggleChange(rowIndex, 'isMembershipPermissionUpdate', value);
                            }}
                        />
                    );
                },
                size: 200,
                enableSorting: false,
            }),
            columnHelper.accessor('isPastSchedulePermissionUpdate', {
                id: 'isPastSchedulePermissionUpdate',
                header: () => (
                    <Row className="flex items-center">
                        <RegularText fontSize={16} lineHeight={20} color={colors.Text_1} className="mr-[10px]">
                            지난 스케줄 수정 권한
                        </RegularText>
                        <TooltipWrapper
                            tooltip={{
                                opacity: 1,
                                customContent: (
                                    <Col>
                                        <DemiLightText fontSize={13} lineHeight={18} color={colors.white}>
                                            {`* 해당 권한이 켜진 경우, `}
                                            <MediumText fontSize={13} lineHeight={18} color={colors.white}>
                                                강사가 과거 수업
                                            </MediumText>
                                        </DemiLightText>
                                        <MediumText
                                            fontSize={13}
                                            lineHeight={20}
                                            color={colors.white}
                                            // className="mb-[16px]"
                                        >
                                            정보를 수정(등록/변경/취소)할 수 있습니다.
                                        </MediumText>
                                        {/* <DemiLightText fontSize={13} lineHeight={20} color={colors.white}>
                                            {`* 권한이 꺼진 경우, `}
                                            <MediumText fontSize={13} lineHeight={20} color={colors.white}>
                                                당일 수업 정보까지만
                                            </MediumText>
                                        </DemiLightText>
                                        <MediumText fontSize={13} lineHeight={20} color={colors.white}>
                                            수정(등록/변경/취소)할 수 있습니다.
                                        </MediumText> */}
                                    </Col>
                                ),
                            }}
                        >
                            <SvgIcon name={'SvgInfoInCircle'} size={16} fill={'#B4BCC4'} />
                        </TooltipWrapper>
                    </Row>
                ),
                cell: (info) => {
                    const rowIndex = info.row.index;
                    const currentValue =
                        coachPermissionListClone[rowIndex]?.isPastSchedulePermissionUpdate ??
                        (info.getValue() as boolean);
                    return (
                        <Toggle
                            value={currentValue}
                            onToggle={(value) => {
                                handleToggleChange(rowIndex, 'isPastSchedulePermissionUpdate', value);
                            }}
                        />
                    );
                },
                size: 200,
                enableSorting: false,
            }),
        ],
        [coachPermissionListClone]
    );
    return (
        <>
            <Modal setIsVisible={setIsVisible} whiteViewWidth={920} borderRadius={25} modalCommpont={true}>
                <div className="flex flex-col justify-center p-[36px]">
                    <MediumText fontSize={25} lineHeight={30} color={colors.Text_1} className="mb-[35px]">
                        강사별 권한 설정
                    </MediumText>
                    <Table data={coachPermissionListClone} columns={columns} height={400} className="mb-[26px]" />
                    <Row className="gap-[12px] self-end">
                        <Col
                            className=" justify-center items-center bg-[#F4F5F7] w-[150px] h-[50px] rounded-[9px] cursor-pointer"
                            onClick={() => setIsVisible(false)}
                        >
                            <RegularText fontSize={20} lineHeight={24} color={colors.Text_1}>
                                취소
                            </RegularText>
                        </Col>
                        <Col
                            className=" justify-center items-center bg-primary w-[150px] h-[50px] rounded-[9px] cursor-pointer"
                            onClick={() => {
                                setIsChangeAlert(true);
                            }}
                        >
                            <RegularText fontSize={20} lineHeight={24} color={colors.white}>
                                적용하기
                            </RegularText>
                        </Col>
                    </Row>
                </div>
            </Modal>
            {isChangeAlert && (
                <ModalPermissionAlert setIsVisible={setIsChangeAlert} onClickConfirm={handleApplyChanges} />
            )}
            {isSuccessAlert && (
                <ModalPermissionSuccessAlert
                    setIsVisible={setIsSuccessAlert}
                    onClickConfirm={() => {
                        setIsSuccessAlert(false);
                        setIsVisible(false);
                    }}
                />
            )}
        </>
    );
};

export default ModalCoachPermission;
